import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowsClockwiseLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import GlassCard from './brand/GlassCard';
import Button from './TailwindComponents/Button';
import ActivityPane from './activity/ActivityPane';
import { ActivityEntry, normalizeAuditPayload, toActivityEntries } from './activity/activityModel';

/**
 * The compact "Recent Activity" card on a detail page.
 *
 * It used to draw its own version of the audit log: its own clock-icon rows,
 * its own `bg-gray-200 dark:bg-gray-700` pulse blocks, its own absolute
 * `formatDateTime` stamps. Same records as the full log panel, a different
 * look on every page that showed them. It is a small `ActivityPane` now —
 * the same rail, the same glyphs, the same relative times as the feed.
 */

interface RecentActivityCardProps {
  /** Async function returning either an array of entries or a normalized
   *  { entries: [...] } / { data: [...] } payload. Must accept (limit) so
   *  the card only fetches what it needs. */
  fetchEntries: (limit: number) => Promise<any>;
  /** Optional callback wired to a "View all" button — typically opens the
   *  full slide-in audit log panel for the resource. Hides the button if not
   *  provided. */
  onViewAll?: () => void;
  /** How many entries to show. Default 5. */
  limit?: number;
  /** Override the title (default: "Recent Activity"). */
  title?: string;
}

const RecentActivityCard: React.FC<RecentActivityCardProps> = ({
  fetchEntries,
  onViewAll,
  limit = 5,
  title,
}) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchEntries(limit);
      const { entries: list } = normalizeAuditPayload(result);
      setEntries(toActivityEntries(list).slice(0, limit));
    } catch {
      setEntries([]);
    } finally {
      setIsLoading(false);
    }
    // `fetchEntries` is an inline closure at every call site; depending on it
    // would refetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <GlassCard
      icon={<PiClockCounterClockwiseLight />}
      title={title || t('Recent Activity')}
      padding="none"
      actions={
        <>
          <Button
            variant="neutral"
            size="small"
            onClick={load}
            isLoading={isLoading && entries.length > 0}
            ariaLabel={t('Refresh')}
            icon={<PiArrowsClockwiseLight className="h-4 w-4" />}
          />
          {onViewAll && entries.length > 0 && (
            <Button variant="outline" size="small" onClick={onViewAll}>
              {t('View all')}
            </Button>
          )}
        </>
      }
    >
      <div className="px-5 py-3">
        <ActivityPane
          entries={entries}
          loading={isLoading}
          onRefresh={load}
          density="pane"
          hideToolbar
          emptyState={t('No activity yet.')}
        />
      </div>
    </GlassCard>
  );
};

export default RecentActivityCard;
